export interface StorefrontCustomField {
  name: string;
  type: string;
  required: boolean;
  defaultValue: string;
  placeholder: string;
  regex?: string;
}

function parseCustomFieldsSource(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') {
        const fields = (parsed as { custom_fields?: unknown[]; customFields?: unknown[] }).custom_fields
          ?? (parsed as { custom_fields?: unknown[]; customFields?: unknown[] }).customFields;
        return Array.isArray(fields) ? fields : [];
      }
    } catch {
      return [];
    }

    return [];
  }

  if (raw && typeof raw === 'object') {
    const fields = (raw as { custom_fields?: unknown[]; customFields?: unknown[] }).custom_fields
      ?? (raw as { custom_fields?: unknown[]; customFields?: unknown[] }).customFields;
    return Array.isArray(fields) ? fields : [];
  }

  return [];
}

export function normalizeStorefrontCustomFields(raw: unknown): StorefrontCustomField[] {
  return parseCustomFieldsSource(raw)
    .filter((field): field is Record<string, unknown> => !!field && typeof field === 'object' && !Array.isArray(field))
    .map((field) => {
      const rawType = typeof field.type === 'string' ? field.type.trim().toLowerCase() : 'text';
      const type = rawType.length > 0 ? rawType : 'text';
      const defaultValue = [
        field.default_value,
        field.default,
        field.value,
      ].find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0);

      return {
        name: typeof field.name === 'string' ? field.name.trim() : '',
        type,
        required: field.required === true || field.required === 'true' || field.required === 1 || field.required === '1',
        defaultValue: typeof defaultValue === 'string' ? defaultValue : '',
        placeholder: typeof field.placeholder === 'string' ? field.placeholder : '',
        regex: typeof field.regex === 'string' ? field.regex : undefined,
      };
    })
    .filter((field) => field.name.length > 0 && field.type !== 'hidden');
}

export function isStorefrontCheckboxCustomFieldValueChecked(value: string | undefined): boolean {
  const normalized = value?.trim().toLowerCase() ?? '';
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
}

export function validateStorefrontCustomFieldValue(field: StorefrontCustomField, value: string): string | null {
  if (field.type === 'checkbox') {
    if (field.required && !isStorefrontCheckboxCustomFieldValueChecked(value)) {
      return `${field.name} is required.`;
    }
    return null;
  }

  const normalized = value.trim();
  if (field.required && !normalized) {
    return `${field.name} is required.`;
  }

  if (field.regex && normalized) {
    try {
      const pattern = new RegExp(field.regex);
      if (!pattern.test(normalized)) {
        return `${field.name} has an invalid format.`;
      }
    } catch {
      return null;
    }
  }

  return null;
}

export function buildStorefrontCustomFieldPayload(
  fields: StorefrontCustomField[],
  values: Record<string, string>,
): Record<string, string> {
  const nextValues: Record<string, string> = {};

  fields.forEach((field) => {
    const rawValue = values[field.name] ?? field.defaultValue ?? '';

    if (field.type === 'checkbox') {
      if (isStorefrontCheckboxCustomFieldValueChecked(rawValue)) {
        nextValues[field.name] = 'true';
      }
      return;
    }

    const normalized = rawValue.trim();
    if (normalized.length > 0) {
      nextValues[field.name] = normalized;
    }
  });

  return nextValues;
}
